import { coverSrc } from "@/lib/cover";
import React from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Headphones, Smartphone, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { DiscountBox } from "./DiscountBox";
import { PaymentLogoStrip } from "./PaymentMethods";

const FORMAT_ICONS = { ebook: BookOpen, audiobook: Headphones, aplikacja: Smartphone };

export function CartDrawer() {
  const { items, remove, total, subtotal, discount, discountAmount, open, setOpen } = useCart();
  const navigate = useNavigate();

  if (!open) return null;

  const goToCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto py-6" data-testid="cart-drawer">
      <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative mx-auto flex min-h-full w-full max-w-lg items-center px-4">
      <section className="relative flex max-h-[calc(100vh-3rem)] w-full flex-col overflow-hidden rounded-3xl border border-cyan-500/15 bg-slate-950 shadow-2xl shadow-cyan-500/10">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
          <h2 className="font-display text-lg font-bold">Twój koszyk</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close-button" className="rounded-full p-2 text-slate-400 transition-colors hover:text-white" aria-label="Zamknij koszyk">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-6">
          {items.length === 0 && <p className="text-center text-sm text-slate-500">Koszyk jest pusty. Czas to zmienić!</p>}
          {items.map((i) => {
            const Icon = FORMAT_ICONS[i.format] || BookOpen;
            return (
              <div key={i.id} className="flex items-center gap-3.5 rounded-xl glass-panel p-3" data-testid={`cart-item-${i.id}`}>
                {i.cover_image ? (
                  <img src={coverSrc(i)} alt={i.title} className="h-16 w-12 shrink-0 rounded-lg object-cover shadow-lg" />
                ) : (
                  <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-400">
                    <Icon className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-100">{i.title}</div>
                  <div className="text-xs text-slate-400">{i.author}</div>
                  <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{i.format} · 1 szt.</div>
                </div>
                <div className="text-sm font-bold text-cyan-300">{i.price.toFixed(2)} zł</div>
                <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="rounded-lg p-1.5 text-slate-500 transition-colors hover:text-rose-400" aria-label="Usuń">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-800 p-6">
          {items.length > 0 && <div className="mb-4"><DiscountBox testidPrefix="cart-discount" /></div>}
          {discount && items.length > 0 && (
            <div className="mb-2 space-y-1 text-sm">
              <div className="flex items-center justify-between text-slate-400"><span>Suma</span><span>{subtotal.toFixed(2)} zł</span></div>
              <div className="flex items-center justify-between text-emerald-300" data-testid="cart-discount-line"><span>Rabat −{discount.percent}%</span><span>−{discountAmount.toFixed(2)} zł</span></div>
            </div>
          )}
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-slate-400">Razem</span>
            <span data-testid="cart-total" className="font-display text-xl font-extrabold text-white">{total.toFixed(2)} zł</span>
          </div>
          <button
            data-testid="cart-checkout-button"
            onClick={goToCheckout}
            disabled={!items.length}
            className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 font-display text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
          >
            Przejdź do zamówienia
          </button>
          <div className="mt-4"><PaymentLogoStrip /></div>
        </div>
      </section>
      </div>
    </div>
  );
}
